/**
 * Bankruptcy Review Flow
 * 
 * Auto Starter bankruptcy recommendation flow.
 * Maps advanced backend lanes to simple user-facing labels:
 * - Factual Dispute
 * - Metro 2 Review
 * - Public Record Review
 * - Human Review
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import axios from 'axios';
import {
  Scale,
  FileText,
  Database,
  AlertTriangle,
  User,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Shield,
  Loader2,
  Info,
  AlertCircle,
  Lock,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// BACKEND TO FRONTEND BANKRUPTCY MAPPING
// ============================================

const BANKRUPTCY_LANE_MAPPING = {
  'post_bankruptcy_tradeline_factual': {
    label: 'Factual Dispute',
    icon: FileText,
    color: 'emerald',
    reason: 'One or more bankruptcy-related accounts appear to be reporting inaccurate balance, status, comment, or update history.',
  },
  'post_bankruptcy_tradeline_metro2': {
    label: 'Metro 2 Review',
    icon: Database,
    color: 'emerald',
    reason: 'One or more bankruptcy-related accounts appear to contain technical reporting conflicts that may require a more precise review.',
  },
  'bankruptcy_public_record_dispute': {
    label: 'Public Record Review',
    icon: Scale,
    color: 'amber',
    reason: 'The bankruptcy record itself may contain issues such as chapter, filing date, discharge date, case number, court name, duplicate entry, or reporting age.',
  },
  'bankruptcy_obsolete_reporting': {
    label: 'Public Record Review',
    icon: Scale,
    color: 'amber',
    reason: 'This bankruptcy entry appears to exceed the allowable reporting period (Chapter 7 = 10 years, Chapter 13 = 7 years from filing).',
  },
  'bankruptcy_wrong_association': {
    label: 'Human Review',
    icon: User,
    color: 'red',
    reason: 'This file may require a more advanced review to determine if accounts were incorrectly associated with the bankruptcy.',
  },
  'bankruptcy_escalation_mov_attorney': {
    label: 'Human Review',
    icon: User,
    color: 'red',
    reason: 'This file may require a more advanced review before the next dispute is generated.',
  },
};

const PATH_CONFIG = {
  'Factual Dispute': {
    icon: FileText,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-800',
    iconBg: 'bg-emerald-100',
  },
  'Metro 2 Review': {
    icon: Database,
    color: 'emerald',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-800',
    iconBg: 'bg-emerald-100',
  },
  'Public Record Review': {
    icon: Scale,
    color: 'amber',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-800',
    iconBg: 'bg-amber-100',
  },
  'Human Review': {
    icon: User,
    color: 'red',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    iconBg: 'bg-red-100',
  },
};

// ============================================
// RECOMMENDATION CARD COMPONENT
// ============================================

const RecommendationCard = ({ path, reason, confidence, isRecommended }) => {
  const config = PATH_CONFIG[path] || PATH_CONFIG['Human Review'];
  const Icon = config.icon;

  return (
    <div 
      className={`relative border-2 rounded-2xl p-6 transition-all ${
        isRecommended 
          ? `${config.borderColor} ${config.bgColor} shadow-md` 
          : 'border-stone-200 bg-white'
      }`}
      data-testid={`recommendation-card-${path.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-6">
          <Badge className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-0 shadow-sm">
            <Sparkles size={12} className="mr-1" />
            Recommended Path
          </Badge>
        </div>
      )}

      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl ${config.iconBg} flex items-center justify-center`}>
          <Icon size={24} className={config.textColor} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-stone-900 mb-1">{path}</h3>
          <p className="text-sm text-stone-600 mb-3">{reason}</p>
          
          {/* Confidence indicator */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  confidence >= 80 ? 'bg-emerald-500' : 
                  confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${confidence}%` }}
              />
            </div>
            <span className="text-sm font-medium text-stone-600">{confidence}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// WHY THIS FITS PANEL COMPONENT
// ============================================

const WhyThisFitsPanel = ({ details, isExpanded, onToggle }) => {
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
      <button 
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
        data-testid="why-this-fits-toggle"
      >
        <div className="flex items-center gap-3">
          <Info size={18} className="text-stone-500" />
          <span className="font-medium text-stone-900">Why this fits your report</span>
        </div>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      
      {isExpanded && (
        <div className="px-5 pb-5 border-t border-stone-100" data-testid="why-this-fits-content">
          <div className="grid md:grid-cols-2 gap-4 pt-4">
            {details.bankruptcyType && (
              <div className="bg-stone-50 rounded-lg p-3">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Bankruptcy Type</span>
                <p className="font-semibold text-stone-900 mt-1">{details.bankruptcyType}</p>
              </div>
            )}
            {details.issueClassification && (
              <div className="bg-stone-50 rounded-lg p-3">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Issue Classification</span>
                <p className="font-semibold text-stone-900 mt-1">{details.issueClassification}</p>
              </div>
            )}
            {details.impactedArea && (
              <div className="bg-stone-50 rounded-lg p-3">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Impacted Area</span>
                <p className="font-semibold text-stone-900 mt-1">{details.impactedArea}</p>
              </div>
            )}
            {details.confidenceLevel && (
              <div className="bg-stone-50 rounded-lg p-3">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Confidence Level</span>
                <p className="font-semibold text-stone-900 mt-1">{details.confidenceLevel}</p>
              </div>
            )}
            {details.humanReviewStatus && (
              <div className="bg-stone-50 rounded-lg p-3">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Human Review Status</span>
                <p className="font-semibold text-stone-900 mt-1">{details.humanReviewStatus}</p>
              </div>
            )}
            {details.backendLaneMapping && (
              <div className="bg-stone-50 rounded-lg p-3">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Backend Lane</span>
                <p className="font-mono text-sm text-stone-700 mt-1">{details.backendLaneMapping}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// DELIVERY FORMAT SELECTOR COMPONENT
// ============================================

const DeliveryFormatSelector = ({ selectedFormat, onFormatChange, recommendedFormat }) => {
  const formats = [
    {
      id: 'combined',
      name: 'Combined Letter Package',
      description: 'Best when the actions are compatible, in the same stage, and can move faster together without creating tracking confusion.',
    },
    {
      id: 'separate',
      name: 'Separate Letters',
      description: 'Best when sequencing matters, multiple teams may process the case, or clearer tracking is more important than speed.',
    },
  ];

  return (
    <div className="border border-amber-200 rounded-xl p-5 bg-amber-50/50" data-testid="delivery-format-selector">
      <h4 className="font-bold text-stone-900 mb-1">Delivery Format</h4>
      <p className="text-sm text-stone-600 mb-4">
        The system recommends the best format first, but you can still switch.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {formats.map((format) => (
          <button
            key={format.id}
            onClick={() => onFormatChange(format.id)}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              selectedFormat === format.id
                ? 'border-amber-400 bg-white shadow-sm'
                : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
            data-testid={`format-${format.id}`}
          >
            {recommendedFormat === format.id && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-2">
                Recommended
              </Badge>
            )}
            <h5 className="font-semibold text-stone-900 mb-1">{format.name}</h5>
            <p className="text-sm text-stone-600">{format.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// HUMAN REVIEW LOCK CARD COMPONENT
// ============================================

const HumanReviewLockCard = ({ triggers }) => {
  return (
    <div 
      className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-white rounded-xl p-6"
      data-testid="human-review-lock-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
          <Lock size={24} className="text-red-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-red-900 mb-2">Human Review Required</h3>
          <p className="text-sm text-red-800 mb-4">
            This file needs additional review before generation can continue. The system found conditions 
            that should not be pushed through default automation.
          </p>
          
          {triggers && triggers.length > 0 && (
            <div className="bg-red-100/50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-red-700 font-medium mb-2">Detected Triggers:</p>
              <ul className="space-y-1">
                {triggers.map((trigger, index) => (
                  <li key={index} className="text-sm text-red-800 flex items-center gap-2">
                    <AlertTriangle size={14} />
                    {trigger}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
              View Detection Details
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white">
              Send To Human Review
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN BANKRUPTCY REVIEW FLOW
// ============================================

const BankruptcyReviewFlow = () => {
  const navigate = useNavigate();
  const { user, token, getAuthHeaders } = useAuth();
  const { reportData } = useReportData();

  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(true);
  const [recommendation, setRecommendation] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [deliveryFormat, setDeliveryFormat] = useState('combined');
  const [requiresHumanReview, setRequiresHumanReview] = useState(false);
  const [humanReviewTriggers, setHumanReviewTriggers] = useState([]);

  // Analyze bankruptcy on mount
  useEffect(() => {
    analyzeBankruptcy();
  }, [reportData]);

  const analyzeBankruptcy = async () => {
    setAnalyzing(true);
    setLoading(true);

    try {
      // Call backend test-rules endpoint with bankruptcy data
      const bankruptcyInput = {
        has_bankruptcy: true,
        bankruptcy_type: 'chapter_7', // Would come from report parsing
        bankruptcy_public_record: true,
        bankruptcy_attached_tradelines: reportData?.tradelines?.some(t => 
          t.remarks?.toLowerCase().includes('bankruptcy') || 
          t.payment_status?.toLowerCase().includes('bankruptcy')
        ) || false,
        years_since_filing: 5, // Would come from report parsing
        tradeline_nonzero_balance: reportData?.tradelines?.some(t => 
          t.balance > 0 && t.remarks?.toLowerCase().includes('bankruptcy')
        ) || false,
      };

      const response = await axios.post(
        `${API}/rules-methods/test-rules`,
        { input: bankruptcyInput },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      const result = response.data;
      
      // Map backend lane to frontend label
      const backendLane = result.routing?.dispute_lane || 'bankruptcy_public_record_dispute';
      const mapping = BANKRUPTCY_LANE_MAPPING[backendLane] || BANKRUPTCY_LANE_MAPPING['bankruptcy_public_record_dispute'];

      setRecommendation({
        path: mapping.label,
        reason: result.routing?.ai_recommendation_text || mapping.reason,
        confidence: 86, // Would come from backend
        backendLane: backendLane,
        bankruptcyContext: result.bankruptcy_context,
      });

      // Check if human review is required
      if (result.routing?.human_review_required || mapping.label === 'Human Review') {
        setRequiresHumanReview(true);
        setHumanReviewTriggers([
          result.routing?.ai_recommendation_text || 'Contradiction risk or complex case detected',
        ]);
        setDeliveryFormat('separate'); // Recommend separate for human review cases
      } else {
        setDeliveryFormat('combined');
      }

    } catch (error) {
      console.error('Bankruptcy analysis error:', error);
      // Default fallback
      setRecommendation({
        path: 'Public Record Review',
        reason: 'Unable to complete full analysis. Defaulting to Public Record Review path.',
        confidence: 60,
        backendLane: 'bankruptcy_public_record_dispute',
        bankruptcyContext: null,
      });
    } finally {
      setAnalyzing(false);
      setLoading(false);
    }
  };

  const handleGeneratePackage = () => {
    // Navigate to integrated item selection flow with bankruptcy context
    const pathType = recommendation?.path === 'Metro 2 Review' ? 'metro2' : 'factual';
    const format = deliveryFormat === 'combined' ? 'rounds' : 'anytime';
    navigate(`/dispute-wizard/${pathType}/select-items?mode=${format}&bankruptcy=true&lane=${recommendation?.backendLane || ''}`);
  };

  const getDetails = () => ({
    bankruptcyType: recommendation?.bankruptcyContext?.bankruptcy_type?.replace('_', ' ').toUpperCase() || 'Chapter 7',
    issueClassification: recommendation?.path || 'Public Record Review',
    impactedArea: 'Public record entry + attached accounts',
    confidenceLevel: `${recommendation?.confidence || 86}% confidence`,
    humanReviewStatus: requiresHumanReview ? 'Required' : 'Not required at this stage',
    backendLaneMapping: recommendation?.backendLane || 'bankruptcy_public_record_dispute',
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 mb-2">Analyzing Bankruptcy Data</h3>
          <p className="text-stone-600">Classifying issue and evaluating safest path...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="bankruptcy-review-flow">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dispute-wizard')}
            className="mb-4 text-stone-600"
          >
            <ChevronRight size={16} className="rotate-180 mr-1" />
            Back to Dispute Wizard
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Scale size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Bankruptcy Review</h1>
              <p className="text-sm text-stone-600">AI-recommended dispute path</p>
            </div>
          </div>
        </div>

        {/* Step 1: AI Review Message */}
        <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-xl p-5 mb-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <Sparkles size={20} className="text-amber-400" />
            </div>
            <div>
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 mb-2">
                Step 1 — AI Review
              </Badge>
              <h2 className="text-lg font-bold mb-1">
                We reviewed your report and found a likely bankruptcy-related issue.
              </h2>
              <p className="text-stone-300 text-sm">
                To keep the experience simple and safe, the platform recommends one clear path.
              </p>
            </div>
          </div>
        </div>

        {/* Human Review Lock (if required) */}
        {requiresHumanReview && (
          <div className="mb-6">
            <HumanReviewLockCard triggers={humanReviewTriggers} />
          </div>
        )}

        {/* Step 2: Recommendation Card */}
        {!requiresHumanReview && recommendation && (
          <div className="mb-6">
            <p className="text-sm text-stone-500 uppercase tracking-wide mb-3 font-medium">
              Step 2 — Recommended Path
            </p>
            <RecommendationCard
              path={recommendation.path}
              reason={recommendation.reason}
              confidence={recommendation.confidence}
              isRecommended={true}
            />
          </div>
        )}

        {/* Step 3: Why This Fits */}
        {!requiresHumanReview && (
          <div className="mb-6">
            <p className="text-sm text-stone-500 uppercase tracking-wide mb-3 font-medium">
              Step 3 — Why This Fits Your Report
            </p>
            <WhyThisFitsPanel
              details={getDetails()}
              isExpanded={detailsExpanded}
              onToggle={() => setDetailsExpanded(!detailsExpanded)}
            />
          </div>
        )}

        {/* Step 4: Delivery Format */}
        {!requiresHumanReview && (
          <div className="mb-6">
            <p className="text-sm text-stone-500 uppercase tracking-wide mb-3 font-medium">
              Step 4 — Delivery Format
            </p>
            <DeliveryFormatSelector
              selectedFormat={deliveryFormat}
              onFormatChange={setDeliveryFormat}
              recommendedFormat={deliveryFormat}
            />
          </div>
        )}

        {/* Primary CTA */}
        {!requiresHumanReview && (
          <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-5 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-stone-900 mb-1">Ready to continue</h4>
              <p className="text-sm text-stone-600">
                Recommended format: {deliveryFormat === 'combined' ? 'Combined Letter Package' : 'Separate Letters'}
              </p>
            </div>
            <Button 
              onClick={handleGeneratePackage}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-sm"
              data-testid="generate-recommended-package-btn"
            >
              Generate Recommended Package
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankruptcyReviewFlow;
